import type { SuggestionKeyDownProps, SuggestionOptions } from "@tiptap/suggestion";
import type { Component } from "vue";
import { isChangeOrigin } from "@tiptap/extension-collaboration";
import { VueRenderer } from "@tiptap/vue-3";

interface SuggestionComponent {
  onKeyDown?: (props: SuggestionKeyDownProps) => boolean;
  onBeforeExit?: () => Promise<unknown>;
}

function isSuggestionComponent(componentRef: unknown): componentRef is SuggestionComponent {
  if (typeof componentRef !== "object") return false;
  if (componentRef === null) return false;

  return true;
}

// tiptap doesn't export suggestion state type, so just declare it ourselves
export interface SuggestionPluginState {
  active: boolean;
  range: Range;
  query: null | string;
  text: null | string;
  composing: boolean;
  decorationId?: string | null;
}

export function createSuggestionRenderer(component: Component): Omit<SuggestionOptions, "editor"> {
  return {
    shouldShow: ({ transaction: tr }) => {
      // do not show suggestion if the change originated from collaboration
      if (isChangeOrigin(tr)) {
        return false;
      }

      return true;
    },
    render: () => {
      let renderer: VueRenderer | null;

      function unmount() {
        if (!renderer) return;

        // capture into local variable, because renderer might be re-assigned while promise still settles
        const thisRenderer = renderer;

        function cleanup() {
          thisRenderer.destroy();
        }

        const onBeforeExit = isSuggestionComponent(renderer.ref) ? renderer.ref.onBeforeExit : undefined;

        if (onBeforeExit) {
          void onBeforeExit().then(cleanup);
        } else {
          cleanup();
        }

        renderer = null;
      }

      return {
        onStart: props => {
          renderer = new VueRenderer(component, {
            props,
            editor: props.editor,
          });

          if (!renderer.element) {
            throw new Error("Failed to suggestion component: element not found.");
          }
        },

        onUpdate(props) {
          renderer?.updateProps(props);
        },

        onKeyDown(props) {
          let handled;

          // let the component handle the keydown first
          if (isSuggestionComponent(renderer?.ref)) {
            handled = renderer?.ref.onKeyDown?.(props);
          }

          // even if keydown was handled by component internally (closed popup, etc.)
          // return false if it was Escape key, so that tiptap will auto-exit suggestion
          if (handled && props.event.key === "Escape") {
            return false;
          }

          return handled ?? false;
        },

        onExit() {
          unmount();
        },
      };
    },
  };
}
